import React, { useState } from "react";
import { X, Plus, SquarePlus, BadgePlus, CircleX, Terminal } from "lucide-react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { API_BACKEND_URL } from "../../../Config/Config";
import { buildAuthHeader } from "../../../utils/authToken";
import { useNotification } from "../../../hooks";
import { useQueryClient } from "@tanstack/react-query";
import { parseCurlCommand } from "../../../components-new/utils/parseCurl";

const EndpointForm = ({ collectionID, collection, onClose, open = true }) => {
  const queryClient = useQueryClient();
  const getUserDetails = () => {
    const userDetails = localStorage.getItem("userDetail");
    if (!userDetails) throw new Error("No user details found in localStorage");
    return JSON.parse(userDetails);
  };
  const notify = useNotification();

  const [formData, setFormData] = useState({
    name: "",
    path: "",
    method: "",
    summary: "",
    headers: "",
    body: "",
    queryParams: "",
    bodyType: "json",
    bodySubType: "json",
  });

  const [curlCommand, setCurlCommand] = useState("");
  const [showCurlInput, setShowCurlInput] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const extractEndpointPath = (fullUrl, baseUrl) => {
    if (!fullUrl || !baseUrl) return fullUrl;

    try {
      const normalizedBaseUrl = baseUrl.replace(/\/$/, "");

      if (fullUrl.startsWith(normalizedBaseUrl)) {
        const path = fullUrl.substring(normalizedBaseUrl.length);
        return path || "/";
      }

      return fullUrl;
    } catch (error) {
      console.warn("Error extracting endpoint path:", error);
      return fullUrl;
    }
  };

  const handleCurlParse = () => {
    if (!curlCommand.trim()) {
      notify("Please enter a valid CURL command", "warning");
      return;
    }

    try {
      const parsedData = parseCurlCommand(curlCommand);

      if (!parsedData.url) {
        notify("Could not extract URL from CURL command", "error");
        return;
      }

      const endpointPath = extractEndpointPath(parsedData.url, collection?.baseurl);

      let queryParamsString = "";
      if (parsedData.queryParams && Array.isArray(parsedData.queryParams)) {
        const queryParamsObj = {};
        parsedData.queryParams.forEach((param) => {
          if (param.key && param.value !== undefined) {
            queryParamsObj[param.key] = param.value;
          }
        });
        queryParamsString =
          Object.keys(queryParamsObj).length > 0 ? JSON.stringify(queryParamsObj, null, 2) : "";
      }

      let headersString = parsedData.headers || "";
      if (headersString === "{}" || headersString === "null") {
        headersString = "";
      }

      let bodyContent = parsedData.body || "";
      let summaryText = "Endpoint imported from CURL command";
      let bodyTypeValue = parsedData.bodyType || "none";
      let bodySubTypeValue = "json";

      if (parsedData.bodyType === "form-data" && parsedData.formFields) {
        bodyContent = parsedData.body;
        summaryText = `Endpoint imported from CURL command (form-data with ${parsedData.formFields.length} field(s))`;
        bodySubTypeValue = "text";
        const hasFileField = parsedData.formFields.some((f) => f.type === "file");
        if (hasFileField) {
          bodySubTypeValue = "file";
        }
      } else if (parsedData.bodyType === "json") {
        bodySubTypeValue = "json";
      } else if (parsedData.bodyType === "raw") {
        bodyTypeValue = "text";
        bodySubTypeValue = "text";
      }

      setFormData({
        name: `${parsedData.method || "GET"} ${endpointPath}`,
        path: endpointPath,
        method: parsedData.method || "GET",
        summary: summaryText,
        headers: headersString,
        body: bodyContent,
        queryParams: queryParamsString,
        bodyType: bodyTypeValue,
        bodySubType: bodySubTypeValue,
      });

      const extractedInfo = [];
      if (headersString && headersString.trim() !== "") extractedInfo.push("headers");
      if (
        parsedData.bodyType === "form-data" &&
        parsedData.formFields &&
        parsedData.formFields.length > 0
      ) {
        extractedInfo.push(`form-data (${parsedData.formFields.length} fields)`);
      } else if (parsedData.body && parsedData.body.trim() !== "") {
        extractedInfo.push("body");
      }
      if (parsedData.queryParams && parsedData.queryParams.length > 0)
        extractedInfo.push("query parameters");

      const infoText =
        extractedInfo.length > 0
          ? `CURL command parsed successfully! Extracted: ${extractedInfo.join(", ")}`
          : "CURL command parsed successfully!";

      notify(infoText, "success");
      setShowCurlInput(false);
    } catch (error) {
      console.error("Error parsing CURL command:", error);
      notify("Error parsing CURL command. Please check the format.", "error");
    }
  };

  const validateJsonField = (field, fieldName, shouldValidate = true) => {
    if (!field || field.trim() === "") return true; // Empty is valid
    if (!shouldValidate) return true; // Skip validation if not needed

    try {
      JSON.parse(field);
      return true;
    } catch (error) {
      notify(`Invalid JSON format in ${fieldName}. Please check the syntax.`, "error");
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.path || !formData.method || !formData.summary) {
      notify("Please fill in all required fields", "warning");
      return;
    }

    if (!validateJsonField(formData.headers, "Headers")) return;

    const shouldValidateBody = formData.bodyType === "json";
    if (!validateJsonField(formData.body, "Request Body", shouldValidateBody)) return;

    if (!validateJsonField(formData.queryParams, "Query Parameters")) return;

    const user = getUserDetails();

    const endpointData = {
      name: formData.name,
      path: formData.path,
      method: formData.method,
      summary: formData.summary,
      headers: formData.headers || "",
      body: formData.body || "",
      queryParams: formData.queryParams || "",
      bodyType: formData.bodyType || "none",
      bodySubType: formData.bodySubType || "json",
    };

    const url = `${API_BACKEND_URL}/${collectionID}/endpoints?companyid=${user.companyid}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...buildAuthHeader(),
        },
        body: JSON.stringify(endpointData),
      });

      if (response.ok) {
        const result = await response.json();
        notify("New endpoint added successfully!", "success");
        queryClient.invalidateQueries(["latestAPIResults"]);
        setCurlCommand("");
        onClose();
      } else {
        notify(`Failed to add endpoint ${response.statusText}`, "error");
      }
    } catch (error) {
      notify("Error adding endpoint", "error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span className="font-apple flex items-center text-md text-gray-800">
          <p>Add New API</p>
          <BadgePlus className="ml-2" />
        </span>
        <button
          onClick={onClose}
          className="flex items-center justify-center p-1.5 rounded hover:bg-gray-200 transition-all duration-200 cursor-pointer"
        >
          <CircleX size={20} className="text-red-500" />
        </button>
      </DialogTitle>

      <DialogContent
        sx={{
          marginTop: "20px",
          backgroundColor: "#ffffff",
        }}
      >
        <form onSubmit={handleSubmit} className="font-apple">
          <div className="space-y-4">
            {/* CURL Import Section */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700 flex items-center">
                  <Terminal size={16} className="mr-2" />
                  Import from CURL Command
                </h3>
                <button
                  type="button"
                  onClick={() => setShowCurlInput(!showCurlInput)}
                  className="text-sm text-blue-600 hover:text-blue-700 cursor-pointer"
                >
                  {showCurlInput ? "Hide" : "Show"} CURL Input
                </button>
              </div>

              {showCurlInput && (
                <div className="space-y-3">
                  <textarea
                    value={curlCommand}
                    onChange={(e) => setCurlCommand(e.target.value)}
                    placeholder="Paste your CURL command here..."
                    className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm font-mono resize-none"
                    rows={4}
                  />
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleCurlParse}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-all duration-200 text-sm cursor-pointer"
                    >
                      Parse CURL
                    </button>
                  </div>
                </div>
              )}

              {collection?.baseurl && (
                <div className="mt-2 text-xs text-gray-600">
                  <span className="font-medium">Base URL:</span> {collection.baseurl}
                </div>
              )}
            </div>

            {/* Manual Input Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Endpoint Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                placeholder="Enter endpoint name (e.g., Get Users, Create User)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Path <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="path"
                  value={formData.path}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                  placeholder="Enter API path (e.g., /users or /api/v1/users)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Method <span className="text-red-500">*</span>
                </label>
                <select
                  name="method"
                  value={formData.method}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm cursor-pointer"
                >
                  <option value="" disabled>
                    Select method
                  </option>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Summary <span className="text-red-500">*</span>
              </label>
              <textarea
                name="summary"
                value={formData.summary}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm resize-none"
                placeholder="Enter API summary or description"
              />
            </div>

            {/* Headers Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Headers</label>
              <textarea
                name="headers"
                value={formData.headers}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm font-mono resize-none"
                placeholder='Enter headers as JSON (e.g., {"Content-Type": "application/json", "Authorization": "Bearer token"})'
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter headers as JSON object. Leave empty if no headers needed.
              </p>
            </div>

            {/* Body Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Request Body</label>
              <textarea
                name="body"
                value={formData.body}
                onChange={handleChange}
                rows={6}
                className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm font-mono resize-none"
                placeholder='Enter request body as JSON (e.g., {"name": "John", "email": "john@example.com"})'
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter request body as JSON. Leave empty for GET requests or when no body is needed.
              </p>
            </div>

            {/* Query Parameters Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Query Parameters
              </label>
              <textarea
                name="queryParams"
                value={formData.queryParams}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm font-mono resize-none"
                placeholder='Enter query parameters as JSON (e.g., {"page": "2", "limit": "10"})'
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter query parameters as JSON object. Leave empty if no query parameters needed.
              </p>
            </div>
          </div>
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          gap: "12px",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-all duration-200 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-all duration-200 text-sm cursor-pointer"
        >
          <Plus size={16} className="mr-2" />
          Add API
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default EndpointForm;
